import { Component, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { ThemeProvider } from "@/lib/theme";
import { initLenis, scrollToTopImmediate } from "@/lib/lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";

// Pages secondaires en chargement différé — l'accueil reste eager
// pour éviter un second aller-retour réseau avant le premier affichage.
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Adhesions = lazy(() => import("@/pages/Adhesions"));
const Evenements = lazy(() => import("@/pages/Evenements"));
const Application = lazy(() => import("@/pages/Application"));
const Partenaires = lazy(() => import("@/pages/Partenaires"));
const Faq = lazy(() => import("@/pages/Faq"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Founders = lazy(() => import("@/pages/Founders"));
const BusinessClubVaucluse = lazy(() => import("@/pages/BusinessClubVaucluse"));
const ClubMotardsVaucluse = lazy(() => import("@/pages/ClubMotardsVaucluse"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const MembrePro = lazy(() => import("@/pages/Formules").then((m) => ({ default: m.MembrePro })));
const Premium = lazy(() => import("@/pages/Formules").then((m) => ({ default: m.Premium })));
const Legal = lazy(() => import("@/pages/Legal"));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-center">
          <p className="font-heading text-2xl font-bold text-gold">MBC84</p>
          <p className="text-sm text-muted-foreground">Une erreur est survenue. Rechargez la page pour continuer.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteEffects() {
  const { pathname, state } = useLocation();
  useEffect(() => {
    const main = document.querySelector("main");
    if (main && !main.id) main.id = "contenu";
    if (!state?.scrollTo) scrollToTopImmediate();
  }, [pathname, state]);
  useEffect(() => {
    initLenis();
  }, []);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <RouteEffects />
          <a
            href="#contenu"
            data-testid="skip-link"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-full focus:bg-gold focus:px-5 focus:py-2.5 focus:text-xs focus:font-bold focus:uppercase focus:tracking-wide focus:text-night"
          >
            Aller au contenu principal
          </a>
          <Navbar />
          <Suspense fallback={<div className="min-h-[60vh] bg-background" aria-hidden="true" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/le-club" element={<About />} />
              <Route path="/business-club-vaucluse" element={<BusinessClubVaucluse />} />
              <Route path="/club-motards-vaucluse" element={<ClubMotardsVaucluse />} />
              <Route path="/bientot" element={<ComingSoon />} />
              <Route path="/nos-fondateurs" element={<Founders />} />
              <Route path="/application" element={<Application />} />
              <Route path="/adhesion" element={<Adhesions />} />
              <Route path="/membre-pro" element={<MembrePro />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/partenaires" element={<Partenaires />} />
              <Route path="/evenements" element={<Evenements />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/mentions-legales" element={<Legal type="mentions" />} />
              <Route path="/confidentialite" element={<Legal type="confidentialite" />} />
              <Route path="/cgv" element={<Legal type="cgv" />} />
              <Route path="/cookies" element={<Legal type="cookies" />} />
              <Route path="/a-propos" element={<Navigate to="/le-club" replace />} />
              <Route path="/adhesions" element={<Navigate to="/adhesion" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer />
          <CookieBanner />
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
